import isMatch from 'lodash/isMatch'
import maxBy from 'lodash/maxBy'
import sortBy from 'lodash/sortBy'
import toPairs from 'lodash/toPairs'
import type { DocumentDetail } from '@klear/document/trader-document'
import type { Money } from '@klear/money'
import type { OrderEventContributions } from '@klear/order-event/document-contribution.wire'
import type { EventTimelineRow } from '@klear/order-event/order-event-timeline-row'
import type { DocumentReviewProposal } from '@klear/schema/document-review'
import type {
  DocumentDoubtDisposition,
  DocumentDoubtKind,
  DocumentExtraction,
  DocumentExtractionConfidence
} from '@klear/schema/document-processing'
import { Timestamp } from '@klear/time'
import { parseTimestamp } from '@klear/time/boundary'
import type { QuoteHighlight } from '@/app/document-review/pdf-document-view'

type FiledReviewCheck = {
  field: string | null
  kind: DocumentDoubtKind
  disposition: DocumentDoubtDisposition
  message: string
  answer: string | null
}

type FiledReviewQuote = {
  field: string
  rank: number
  page: number
  quote: string
  confidence: DocumentExtractionConfidence
}

export type FiledReview = {
  reviewId: string
  orderEventDocumentId: string | null
  uploadedFilename: string | null
  filedAt: Timestamp | null
  filedBy: string | null
  filedAutomatically: boolean
  checks: FiledReviewCheck[]
  quotes: FiledReviewQuote[]
}

type AmountEdit = {
  at: Timestamp
  by: string | null
}

export type RecordReviewChecks = {
  reviews: FiledReview[]
  documentsTotal: string | null
  documentsFiledAt: Timestamp | null
  lastAmountEdit: AmountEdit | null
}

export type AmountChange = {
  documentsTotal: string
  changedBy: string | null
  changedAt: Timestamp
}

export type SheetCheck = FiledReviewCheck & { reviewId: string; quote: string | null }

export type SheetChecks = {
  byField: ReadonlyMap<string, SheetCheck[]>
  fieldless: SheetCheck[]
}

function quotesOf(extraction: DocumentExtraction): FiledReviewQuote[] {
  return toPairs(extraction).flatMap(([field, entry]) =>
    (entry?.sources ?? []).map((source, rank) => ({
      field,
      rank,
      page: source.page,
      quote: source.quote,
      confidence: entry.confidence
    }))
  )
}

function filedReviewOf(
  detail: DocumentDetail,
  run: NonNullable<DocumentDetail['latestRun']>,
  proposal: DocumentReviewProposal,
  reviewerNames: ReadonlyMap<number, string>
): FiledReview {
  const answers = detail.decision?.answers ?? []
  const filedAutomatically = detail.decision?.filedAutomatically ?? false
  const reviewedBy = detail.decision?.reviewedBy ?? null
  return {
    reviewId: run.id,
    orderEventDocumentId: proposal.orderEventDocumentId,
    uploadedFilename: detail.document.name,
    filedAt: run.reviewedAt,
    filedBy: filedAutomatically || reviewedBy == null ? null : (reviewerNames.get(reviewedBy) ?? null),
    filedAutomatically,
    checks: proposal.doubts.map((doubt) => ({
      field: doubt.field,
      kind: doubt.kind,
      disposition: doubt.disposition,
      message: doubt.message,
      answer:
        answers.find((answer) => isMatch(answer, { doubtKind: doubt.kind, field: doubt.field, message: doubt.message }))
          ?.answer ?? null
    })),
    quotes: quotesOf(proposal.extraction)
  }
}

export function recordReviewChecks(input: {
  recordId: number
  documentIds: readonly string[]
  details: readonly DocumentDetail[]
  reviewerNames: ReadonlyMap<number, string>
  contributions: OrderEventContributions | undefined
  amountEdits: readonly EventTimelineRow[]
}): RecordReviewChecks {
  const { recordId, documentIds, details, reviewerNames, contributions, amountEdits } = input
  const reviews = sortBy(details, (detail) => -(detail.latestRun?.reviewedAt?.valueOf() ?? 0)).flatMap((detail) => {
    const run = detail.latestRun
    if (run === null) return []
    return (detail.proposals ?? [])
      .filter(
        (proposal) =>
          proposal.orderEventId === recordId &&
          (proposal.orderEventDocumentId == null || documentIds.includes(proposal.orderEventDocumentId))
      )
      .map((proposal) => filedReviewOf(detail, run, proposal, reviewerNames))
  })
  const lastFiled = maxBy(
    (contributions?.contributions ?? []).filter((contribution) => contribution.amount != null),
    (contribution) => contribution.confirmedAt
  )
  const lastEdit = maxBy(
    amountEdits.flatMap((row) => {
      const at = parseTimestamp(row.recordedAt)
      return row.eventId === recordId && row.changedKeys.includes('amount') && at != null
        ? [{ at, by: row.byUserName }]
        : []
    }),
    (edit) => edit.at.valueOf()
  )
  return {
    reviews,
    documentsTotal: contributions?.rollup.totalAmount ?? null,
    documentsFiledAt: lastFiled == null ? null : Timestamp.fromEpochMs(lastFiled.confirmedAt),
    lastAmountEdit: lastEdit ?? null
  }
}

export function amountChangeOf(checks: RecordReviewChecks | undefined, amount: Money | null): AmountChange | null {
  if (checks == null || checks.reviews.length === 0) return null
  const { documentsTotal, documentsFiledAt, lastAmountEdit } = checks
  if (amount == null || documentsTotal == null || amount.toJSON() === documentsTotal) return null
  if (lastAmountEdit == null) return null
  if (documentsFiledAt != null && !lastAmountEdit.at.isAfter(documentsFiledAt)) return null
  return { documentsTotal, changedBy: lastAmountEdit.by, changedAt: lastAmountEdit.at }
}

function sheetField(field: string | null, fieldKeys: readonly string[]): string | null {
  const key = field === 'order' ? 'customer' : field
  return key != null && fieldKeys.includes(key) ? key : null
}

export function firstQuoteOf(review: FiledReview | undefined, field: string | null): FiledReviewQuote | undefined {
  return review?.quotes.find((quote) => quote.field === field)
}

export function sheetChecks(record: RecordReviewChecks | undefined, fieldKeys: readonly string[]): SheetChecks {
  const checks = sortBy(
    (record?.reviews ?? []).flatMap((review) =>
      review.checks.map((check) => ({
        ...check,
        reviewId: review.reviewId,
        quote: firstQuoteOf(review, check.field)?.quote ?? null
      }))
    ),
    (check) => (check.disposition === 'needs_answer' ? 0 : 1)
  )
  const byField = new Map<string, SheetCheck[]>()
  const fieldless: SheetCheck[] = []
  for (const check of checks) {
    const key = sheetField(check.field, fieldKeys)
    if (key == null) fieldless.push(check)
    else byField.set(key, [...(byField.get(key) ?? []), check])
  }
  return {
    byField,
    fieldless
  }
}

export function reviewOfDocument(
  record: RecordReviewChecks | undefined,
  documentId: string | undefined
): FiledReview | undefined {
  return record?.reviews.find((review) => review.orderEventDocumentId === documentId)
}

export function highlightsOf(review: FiledReview | undefined): QuoteHighlight[] {
  if (review == null) return []
  return review.quotes.map((quote) => ({
    key: quote.field,
    page: quote.page,
    rank: quote.rank,
    quote: quote.quote,
    confidence: quote.confidence
  }))
}

export function checkTag(check: FiledReviewCheck): 'Answered' | 'Noted' {
  return check.disposition === 'needs_answer' ? 'Answered' : 'Noted'
}
